import { ClientPreview, DocumentPreview } from '@/modules/shared/feature-bridges.ts';
import { defineStore } from 'pinia';
import { StoreId } from '@/modules/common/enums/store-id.enum.ts';

type PreviewInterface = {
  setEntityId(entityId: number | null): PreviewInterface;
  fetchDataPreview(): Promise<unknown>;
  fetchPartlyPreview(endPoint: string): Promise<unknown> | void;
};

type PreviewInterfaceCtor = {
  new (entityId: number | null): PreviewInterface;
  intefaceIdent: string;
};

const interfaces: Record<string, PreviewInterfaceCtor> = {
  [DocumentPreview.intefaceIdent]: DocumentPreview as PreviewInterfaceCtor,
  [ClientPreview.intefaceIdent]: ClientPreview as PreviewInterfaceCtor,
};

type PreviewState = {
  entityIdPreview: number | null;
  isActivePreview: boolean;
  initFirstDataPreview: boolean;
  interfacePreview: PreviewInterface | null;
  _pendingInitPromise: Promise<unknown> | null;
  _pendingInitEntityId: number | null;
};

/** @deprecated */
export const usePreviewStore = defineStore(StoreId.Preview, {
  state: (): PreviewState => ({
    entityIdPreview: null,
    isActivePreview: false,
    initFirstDataPreview: false,
    interfacePreview: null,
    _pendingInitPromise: null,
    _pendingInitEntityId: null,
  }),
  getters: {
    hasEntityIdPreviewGetter(state): boolean {
      return state.entityIdPreview !== null;
    },
  },
  actions: {
    setEntityIdPreviewAction(entityId: number | null) {
      this.entityIdPreview = entityId;
      if (!entityId) {
        this.isActivePreview = false;
        this.initFirstDataPreview = false;
      }
    },
    setActivePreviewAction(isActive = true) {
      this.isActivePreview = isActive;
    },
    initPreviewAction(interfacePreview: string) {
      if (!interfaces[interfacePreview]) {
        throw new Error(`Unknown interface ${interfacePreview}.`);
      }

      const hasPendingInitForSameEntity =
        this._pendingInitPromise && this._pendingInitEntityId === this.entityIdPreview;

      if (hasPendingInitForSameEntity) {
        return this._pendingInitPromise;
      }

      const ClassName = interfaces[interfacePreview];
      const preview = new ClassName(this.entityIdPreview);

      this.interfacePreview = preview;
      this.initFirstDataPreview = false;
      this._pendingInitEntityId = this.entityIdPreview;

      this._pendingInitPromise = preview
        .setEntityId(this.entityIdPreview)
        .fetchDataPreview()
        .finally(() => {
          this._pendingInitPromise = null;
          this._pendingInitEntityId = null;
          if (this.entityIdPreview !== null) {
            this.initFirstDataPreview = true;
          }
          this._pendingInitPromise = null;
          this._pendingInitEntityId = null;
        });

      return this._pendingInitPromise;
    },
    refreshPartlyPreviewAction(endPoint: string, documentId = -1) {
      const preview = this.interfacePreview as PreviewInterface | null;

      if (!preview) {
        return;
      }

      if (documentId === -1 || documentId === this.entityIdPreview) {
        return preview.setEntityId(this.entityIdPreview).fetchPartlyPreview(endPoint);
      }
    },
    refreshPreviewAction() {
      const preview = this.interfacePreview as PreviewInterface | null;

      if (!preview) {
        return;
      }

      return preview
        .setEntityId(this.entityIdPreview)
        .fetchDataPreview()
        .finally(() => {
          if (this.entityIdPreview !== null) {
            this.initFirstDataPreview = true;
          }
        });
    },
  },
});
